<template>
  <Teleport to="body">
    <transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-2"
    >
      <div
        v-if="open && pageId"
        class="fixed inset-0 z-[1900]"
        role="dialog"
        aria-modal="true"
        aria-label="Checkout order form page editor"
      >
        <SitePageEditorLayout
          :key="`checkout-page-${pageId}`"
          :page-id="pageId"
          :cart-product-id="cartProductId"
          :required-system-elements="requiredElements"
          :overlay-z-base="1950"
          autosave
          embedded
          embedded-back-label="Back to Product Builder"
          @exit-embedded="emit('close')"
          @draft-saved="emit('draft-saved', $event)"
        />
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue';
import SitePageEditorLayout from '@/layouts/SitePageEditorLayout.vue';

const props = defineProps({
  open: { type: Boolean, default: false },
  pageId: { type: [String, Number], default: null },
  cartProductId: { type: [String, Number], default: null },
  productName: { type: String, default: '' },
});

const emit = defineEmits(['close', 'draft-saved']);
const requiredElements = computed(() => ([{
  type: 'checkout-order-form',
  props: {
    systemManaged: true,
    checkoutMode: 'one_step',
    editorPreviewStep: 'contact',
    heading: 'Complete your order',
    contactStepLabel: 'Your information',
    paymentStepLabel: 'Payment details',
    continueButtonLabel: 'Continue to secure payment',
    buttonLabel: 'Complete purchase',
    firstNameLabel: 'First name',
    lastNameLabel: 'Last name',
    emailLabel: 'Email address',
    collectCompany: false,
    companyLabel: 'Company',
    collectPhone: true,
    phoneLabel: 'Phone number',
    phoneRequired: false,
    smsConsentEnabled: false,
    smsConsentLabel: 'I agree to receive transactional text messages about this purchase. Message and data rates may apply.',
    collectShippingAddress: false,
    shippingHeading: 'Shipping address',
    addressLabel: 'Street address',
    cityLabel: 'City',
    stateLabel: 'State / province',
    postalCodeLabel: 'Postal code',
    countryLabel: 'Country',
    productSelectorEnabled: false,
    productSelectorHeading: 'Select a payment plan',
        productSelectorOptions: [{ id: 'primary-offer', productPublicId: '', title: 'Current product', subtitle: '', badge: '', paymentType: 'one_time', priceCents: 0, currencyCode: 'USD' }],
    orderBumpEnabled: false,
    orderBumpProductPublicId: '',
    orderBumpHeadline: 'Yes, add this to my order',
    orderBumpDescription: 'Add this special offer to your purchase.',
    orderBumpSelectedByDefault: false,
    termsEnabled: false,
    termsLabel: 'I agree to the terms of purchase and privacy policy.',
    showSecureBadge: true,
    secureBadgeText: 'Secure checkout',
    secureBadgeIcon: 'lock',
    secureBadgeTextColor: '#047857',
    secureBadgeBackgroundColor: '#ECFDF5',
    cardBackgroundColor: '#FFFFFF',
    cardTransparent: false,
    cardBorderColor: '#E4E4E7',
    cardBorderWidth: 1,
    cardCornerRadius: 16,
    cardShadow: 'medium',
    cardPaddingX: 36,
    cardPaddingY: 36,
    showTrustBadges: true,
    inputBackgroundColor: '#FFFFFF',
    inputTextColor: '#18181B',
    inputBorderColor: '#D4D4D8',
    inputLabelColor: '#3F3F46',
    inputHeight: 48,
    formCornerRadius: 12,
    inputShadow: false,
    buttonBackgroundColor: '#2D8CFF',
    buttonTextColor: '#FFFFFF',
    buttonFontWeight: 800,
    buttonFontSize: 14,
    buttonCornerRadius: 12,
    buttonShadow: true,
    buttonSubtext: '',
    productName: props.productName || 'Your product',
    elementWidthPercent: 100,
    elementHeight: 540,
    elementHeightUnit: 'px',
    elementAlign: 'center',
    elementPadding: 0,
    elementPaddingX: 0,
    elementPaddingY: 0,
    elementMargin: 0,
    elementBackgroundColor: 'transparent',
    rowElementColumnIndex: 0,
  },
}]));
</script>
